import type { User } from "../types";

/**
 * Subscription state of a user, in one place.
 *
 * A trainer can pause someone's subscription for as long as needed. While
 * it's paused, the days left are frozen (kept in `subscriptionPause`), the
 * subscription doesn't run out, and the person can only use the feed and the
 * chat, unless the trainer left them full access. Resuming starts the clock
 * again from the days that were left.
 */

export const DAY_MS = 24 * 60 * 60 * 1000;

/** The only screens a paused user can open (plus their settings). */
export const PAUSED_PATHS = ["/feed", "/chat", "/settings"];

export function isPaused(user: Pick<User, "subscriptionPause"> | null | undefined): boolean {
  return !!user?.subscriptionPause?.since;
}

/** Whether a regular user may use the app (staff always may). */
export function hasActiveSubscription(user: User): boolean {
  if (user.role !== "user") return true;
  if (isPaused(user)) return true;
  return !!user.subscriptionEndDate && new Date(user.subscriptionEndDate) > new Date();
}

/** Paused without the trainer's "keep full access" exception. */
export function isLimitedByPause(user: User | null | undefined): boolean {
  return !!user && user.role === "user" && isPaused(user) && !user.subscriptionPause?.fullAccess;
}

/** Subscription time left in ms: frozen while paused. */
export function subscriptionTimeLeft(user: Pick<User, "subscriptionEndDate" | "subscriptionPause">): number {
  if (user.subscriptionPause?.since) return Math.max(0, user.subscriptionPause.remainingMs || 0);
  const end = user.subscriptionEndDate ? Date.parse(user.subscriptionEndDate) : 0;
  return Math.max(0, end - Date.now());
}

/** Whole days left (rounded up, so "a few hours" still counts as a day). */
export function subscriptionDaysLeft(user: Pick<User, "subscriptionEndDate" | "subscriptionPause">): number {
  return Math.ceil(subscriptionTimeLeft(user) / DAY_MS);
}
